// Baselayer Background Checks — Results Explorer add-on hooks. Query-key
// factory + add-ons query and order/refresh/download mutations. Each mutation
// invalidates both the add-ons list and the check detail (status flips +
// rawResponseJson land there), mirroring useBackgroundChecks conventions.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addOnsApi,
  type BackgroundCheckAddOnDto,
  type BackgroundCheckAddOnType,
  type OrderDocketDetailRequest,
  type OrderLiensRequest,
  type OrderLitigationsRequest,
} from "@/api/backgroundChecks/addOnsApi";
import { backgroundCheckKeys } from "./useBackgroundChecks";

// ============================== query keys ============================

export const addOnKeys = {
  list: (checkId: string) =>
    ["background-checks", "add-ons", checkId] as const,
};

// ============================== queries ===============================

export function useAddOns(checkId: string | undefined) {
  return useQuery<BackgroundCheckAddOnDto[]>({
    queryKey: addOnKeys.list(checkId ?? ""),
    queryFn: () => addOnsApi.list(checkId!),
    enabled: !!checkId,
  });
}

// ============================== mutations =============================

/** Discriminated order request — drives the right endpoint per add-on type. */
export type OrderAddOnInput =
  | { addOnType: "Liens"; options?: OrderLiensRequest }
  | { addOnType: "Litigations"; options?: OrderLitigationsRequest }
  | { addOnType: "Bankruptcies" }
  | { addOnType: "WebsiteAnalysis" }
  | { addOnType: "ReviewsSocials" }
  | { addOnType: "DocketDetail"; options: OrderDocketDetailRequest };

function invalidate(qc: ReturnType<typeof useQueryClient>, checkId: string) {
  qc.invalidateQueries({ queryKey: addOnKeys.list(checkId) });
  qc.invalidateQueries({ queryKey: backgroundCheckKeys.detail(checkId) });
}

export function useOrderAddOn(checkId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderAddOnInput) => {
      switch (input.addOnType) {
        case "Liens":
          return addOnsApi.orderLiens(checkId, input.options);
        case "Litigations":
          return addOnsApi.orderLitigations(checkId, input.options);
        case "Bankruptcies":
          return addOnsApi.orderBankruptcies(checkId);
        case "WebsiteAnalysis":
          return addOnsApi.orderWebsiteAnalysis(checkId);
        case "ReviewsSocials":
          return addOnsApi.orderReviewsSocials(checkId);
        case "DocketDetail":
          return addOnsApi.orderDocketDetail(checkId, input.options);
      }
    },
    onSuccess: () => invalidate(qc, checkId),
  });
}

export function useRefreshAddOn(checkId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (addonType: BackgroundCheckAddOnType) =>
      addOnsApi.refresh(checkId, addonType),
    onSuccess: () => invalidate(qc, checkId),
  });
}

export function useDownloadLienDocument(checkId: string) {
  return useMutation({
    mutationFn: (lienId: string) =>
      addOnsApi.downloadLienDocument(checkId, lienId),
  });
}
